import {AlertTriangle} from 'lucide-react';
import {useTranslation} from 'react-i18next';

import {Typography} from '@/components/ui/typography';
import {
  type DosageDetails,
  PartOfTheDay,
  type ResidentMedication,
} from '@/services/graphqlHooks';
import {
  getMedicationWithPartOfTheDay,
  type MedicationWithPartOfTheDay,
} from '@/shared/medications/utils/getMedicationWithPartOfTheDay';
import {isMedicationCurrentByEndDate} from '@/shared/medications/utils/medicationDateRange';
import partOfTheDay from '@/translation/partOfTheDay';
import unitType from '@/translation/unitType';

export type AmbulancePrintMedicationsProps = {
  medications: ResidentMedication[];
};

const timeOfDayColumns = [
  PartOfTheDay.Morning,
  PartOfTheDay.Afternoon,
  PartOfTheDay.Evening,
  PartOfTheDay.Night,
] as const;

function formatDosage(dosageDetails: DosageDetails) {
  const dosage = dosageDetails.dosage;
  const unit = unitType[dosage.unit] ?? dosage.unit;

  return [`${dosage.quantity} ${unit}`, dosageDetails.time]
    .filter(Boolean)
    .join(' ');
}

type DosageCellProps = {
  dosageDetails: DosageDetails[];
};

function DosageCell({dosageDetails}: DosageCellProps) {
  if (dosageDetails.length === 0) {
    return <Typography className="text-xs">-</Typography>;
  }

  return (
    <div className="flex flex-col items-center gap-0.5">
      {dosageDetails.map((details, index) => (
        <Typography
          key={`${formatDosage(details)}-${index}`}
          className="min-w-0 text-xs break-words"
        >
          {formatDosage(details)}
        </Typography>
      ))}
    </div>
  );
}

type MedicationGroupProps = {
  title: string;
  medications: MedicationWithPartOfTheDay[];
};

function MedicationGroup({title, medications}: MedicationGroupProps) {
  if (medications.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <Typography variant="h2" asChild className="text-lg font-semibold">
        <h2>{title}</h2>
      </Typography>

      <div className="flex flex-col gap-1">
        <MedicationTableHeader />
        {medications.map(medication => (
          <MedicationTableRow key={medication.id} medication={medication} />
        ))}
      </div>
    </div>
  );
}

function MedicationTableHeader() {
  const {t} = useTranslation();

  return (
    <div className="grid grid-cols-[1.4fr_repeat(4,minmax(0,1fr))] items-center gap-px rounded-full bg-neutral-100 px-4 py-2 text-center text-xs text-neutral-700 print:break-inside-avoid">
      <Typography className="text-xs">{t('Medication')}</Typography>
      {timeOfDayColumns.map(column => (
        <Typography key={column} className="text-xs">
          {partOfTheDay[column]}
        </Typography>
      ))}
    </div>
  );
}

function MedicationTableRow({
  medication,
}: {
  medication: MedicationWithPartOfTheDay;
}) {
  const warning = medication.warnings;

  return (
    <div className="flex [break-inside:avoid] flex-col gap-1 rounded-3xl bg-neutral-50 px-4 py-2 print:break-inside-avoid">
      <div className="grid grid-cols-[1.4fr_repeat(4,minmax(0,1fr))] items-center gap-px text-center">
        <Typography className="min-w-0 text-left text-sm font-semibold break-words">
          {medication.medicationName}
        </Typography>
        {timeOfDayColumns.map(column => (
          <div
            key={column}
            className="border-l border-neutral-300 px-2 text-xs"
          >
            <DosageCell dosageDetails={medication[column] ?? []} />
          </div>
        ))}
      </div>

      {warning && (
        <div className="flex items-start gap-1 text-orange-700">
          <AlertTriangle
            aria-hidden="true"
            className="mt-0.5 size-3 shrink-0 text-orange-600"
          />
          <Typography className="text-[10px] leading-4 text-orange-700">
            {warning}
          </Typography>
        </div>
      )}
    </div>
  );
}

function AmbulancePrintMedications({
  medications,
}: AmbulancePrintMedicationsProps) {
  const {t} = useTranslation();

  if (!medications || medications.length === 0) return null;

  const sortedMedications = getMedicationWithPartOfTheDay(
    [...medications].sort((a, b) =>
      a.medicationName.localeCompare(b.medicationName)
    )
  );
  const currentMedications = sortedMedications.filter(medication =>
    isMedicationCurrentByEndDate(medication.endDate)
  );
  const previousMedications = sortedMedications.filter(
    medication => !isMedicationCurrentByEndDate(medication.endDate)
  );

  return (
    <div className="flex flex-col gap-3">
      <MedicationGroup
        title={t('Current medications')}
        medications={currentMedications}
      />
      <MedicationGroup
        title={t('Old medication from last 3 months')}
        medications={previousMedications}
      />
    </div>
  );
}

export default AmbulancePrintMedications;
